import React, { useContext, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'
import track from '../../lib/track'
import './ExitIntent.scss'
import { splitLocale } from '../../config/i18nRoutes'

const SEEN_KEY = 'mlmind:exit-seen'
const HIDE_ON = ['/contact-us', '/estimate', '/ai-readiness', '/seo-check', '/accessibility-check', '/ai-savings']
const MIN_TIME_ON_SITE = 12000 // ms — never interrupt someone who just arrived

/**
 * One offer, once per session, when the cursor leaves through the top of
 * the window (the tab bar / address bar — the classic "about to leave").
 * Touch devices never fire this signal, so they never see it, and no media
 * query is needed (touch-capable laptops report "hover: none" while still
 * having a mouse). Pages that already are the offer do not show it.
 */
const ExitIntent = () => {
  const { translations: t } = useContext(LanguageContext)
  const { pathname: fullPath } = useLocation()
  const pathname = splitLocale(fullPath).base
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let seen = false
    try {
      seen = window.sessionStorage.getItem(SEEN_KEY) === '1'
    } catch (e) {
      seen = false
    }
    if (seen || HIDE_ON.includes(pathname)) return undefined

    const arrived = Date.now()
    const onLeave = (e) => {
      if (e.clientY > 0) return
      if (Date.now() - arrived < MIN_TIME_ON_SITE) return
      setOpen(true)
      track('exit_intent_shown', { path: pathname })
      try {
        window.sessionStorage.setItem(SEEN_KEY, '1')
      } catch (err) {
        // fine — it just might show once more on another page
      }
      document.removeEventListener('mouseout', onLeave)
    }
    document.addEventListener('mouseout', onLeave)
    return () => document.removeEventListener('mouseout', onLeave)
  }, [pathname])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const go = (path, event) => {
    track(event, { from: 'exit', path: pathname })
    setOpen(false)
    navigate(path)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className='exit-backdrop'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={() => setOpen(false)}
        >
          <motion.div
            className='exit-card'
            role='dialog'
            aria-modal='true'
            aria-labelledby='exit-title'
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type='button' className='exit-close' onClick={() => setOpen(false)} aria-label={t.exitDismiss}>×</button>
            <p className='exit-kicker'>{t.exitKicker}</p>
            <h2 id='exit-title' className='exit-title'>{t.exitTitle}</h2>
            <p className='exit-sub'>{t.exitSub}</p>
            <div className='exit-actions'>
              <button type='button' className='exit-btn' onClick={() => go('/estimate', 'estimator_click')}>
                {t.exitPrimary} <span aria-hidden='true'>→</span>
              </button>
              <button type='button' className='exit-btn exit-btn-ghost' onClick={() => go('/contact-us#book', 'call_click')}>
                {t.exitSecondary}
              </button>
            </div>
            <ul className='exit-points'>
              <li>{t.homeUsp1}</li>
              <li>{t.homeUsp2}</li>
              <li>{t.homeUsp3}</li>
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ExitIntent
